import React, { useEffect, useState, useCallback } from 'react';
import { cashFlowAPI } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Banknote, ArrowDownToLine, ScaleIcon, AlertTriangle, ShieldCheck, Building2 } from 'lucide-react';

const formatPLN = (n) =>
  `${(n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

/**
 * CashFlowWidgets — dashboard cards showing financial state.
 * Used both on /dashboard and /rozliczenia (compact mode).
 *
 * Props:
 *  - compact (bool): smaller layout for embedded use
 *  - showAlerts (bool): show alert banner (default true)
 *  - onClickToSettle (fn): callback when user clicks "Do rozliczenia" card
 */
export default function CashFlowWidgets({ compact = false, showAlerts = true, onClickToSettle }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const r = await cashFlowAPI.get();
      setData(r.data);
    } catch (e) {
      console.error('cash-flow fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60s
    const t = setInterval(fetchData, 60000);
    return () => clearInterval(t);
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={`grid gap-3 ${compact ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4 h-24 bg-slate-50" />
          </Card>
        ))}
      </div>
    );
  }

  if (!data) return null;

  const cards = [
    {
      key: 'cash_in_office',
      label: 'Gotówka w biurze',
      value: formatPLN(data.cash_in_office),
      sub: `${data.cash_payments_count || 0} wpłat nierozlicz.`,
      icon: Banknote,
      tone: data.cash_in_office >= 10000 ? 'amber' : 'navy',
      testId: 'kpi-cash-in-office',
    },
    {
      key: 'today_deposits',
      label: 'Wpłaty dziś',
      value: formatPLN(data.today_deposits),
      sub: `${data.today_settlements_count || 0} rozliczeń`,
      icon: ArrowDownToLine,
      tone: 'emerald',
      testId: 'kpi-today-deposits',
    },
    {
      key: 'to_settle',
      label: 'Do rozliczenia',
      value: formatPLN(data.to_settle_amount),
      sub: `${data.to_settle_count || 0} wpłat`,
      icon: ScaleIcon,
      tone: data.to_settle_count > 0 ? 'gold' : 'slate',
      testId: 'kpi-to-settle',
      clickable: true,
    },
    {
      key: 'to_pay_operators',
      label: 'Do zapłaty operatorom',
      value: formatPLN(data.remaining_to_operators),
      sub: `${data.operator_gap_count || 0} rezerwacji czeka`,
      icon: Building2,
      tone: data.remaining_to_operators > 0 ? 'rose' : 'slate',
      testId: 'kpi-to-pay-operators',
    },
  ];

  const toneClass = {
    navy: 'bg-navy-50 text-navy-800 border-navy-100',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-100',
    gold: 'bg-amber-50 text-amber-800 border-amber-100',
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    rose: 'bg-rose-50 text-rose-800 border-rose-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100',
  };
  const iconBgClass = {
    navy: 'bg-navy-100 text-navy-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    gold: 'bg-amber-100 text-amber-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
    slate: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="space-y-3">
      <div className={`grid gap-3 ${compact ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'}`}>
        {cards.map((c) => {
          const Icon = c.icon;
          const onClick = c.clickable && onClickToSettle ? onClickToSettle : undefined;
          return (
            <Card
              key={c.key}
              className={`border ${toneClass[c.tone]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
              onClick={onClick}
              data-testid={c.testId}
            >
              <CardContent className={`flex items-center gap-3 ${compact ? 'p-3' : 'p-4'}`}>
                <div className={`rounded-full p-2.5 ${iconBgClass[c.tone]}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium opacity-70">{c.label}</div>
                  <div className={`font-bold ${compact ? 'text-lg' : 'text-2xl'}`}>{c.value}</div>
                  <div className="text-xs opacity-60 truncate">{c.sub}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pending approvals badge for admin/kierownik */}
      {data.pending_approvals > 0 && (
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm"
          data-testid="kpi-pending-approvals"
        >
          <ShieldCheck className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>{data.pending_approvals}</strong> płatności czeka na zatwierdzenie
          </span>
        </div>
      )}

      {/* Alerts */}
      {showAlerts && data.alerts && data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 px-3 py-2 rounded-md border text-sm ${
                a.severity === 'warning'
                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                  : 'bg-blue-50 border-blue-200 text-blue-900'
              }`}
              data-testid={`alert-${a.type}`}
            >
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{a.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
